'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import { useEffect, useState } from 'react';
import { Card, CardBody, Row, Col, Form, Label, Input, Alert, FormGroup } from 'reactstrap';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import usePhoneInputLabelLanguage from '../hooks/usePhoneInputLabelLanguage';
import Cta from './CtaBtn';
import { uploadContactForm } from '../api/contact';
import validateMail from '../utils/validateEmailAddress';
import { isValidCompanyName, isValidPersonName, NAME_MAX_LENGTH } from '../utils/validateName';
import { plausibleEvent } from '../utils/plausibleEvent';
import { logSentryError } from '../utils/logSentry';

import 'react-phone-number-input/style.css';
import { Dictionaries, Locale } from '../i18n/i18n-config';
import { getI18nUtils } from '../i18n/i18n-utils';

const defaultInputs = {
  companyName: {
    type: 'text',
    value: '',
    touched: false,
    valid: false,
    minLength: 2,
    maxLength: NAME_MAX_LENGTH,
    intlId: 'contact-form-companyName',
    order: 1,
    required: true,
  },
  firstName: {
    type: 'text',
    value: '',
    touched: false,
    valid: false,
    minLength: 2,
    maxLength: NAME_MAX_LENGTH,
    intlId: 'contact-form-firstName',
    order: 2,
    required: true,
  },
  lastName: {
    type: 'text',
    value: '',
    touched: false,
    valid: false,
    minLength: 2,
    maxLength: NAME_MAX_LENGTH,
    intlId: 'contact-form-lastName',
    order: 3,
    required: true,
  },
  telephone: {
    type: 'tel',
    value: '',
    touched: false,
    valid: false,
    intlId: 'contact-form-phone',
    order: 5,
    required: true,
  },
  email: {
    type: 'email',
    value: '',
    touched: false,
    valid: false,
    intlId: 'contact-form-email',
    order: 4,
    required: true,
  },
  message: {
    type: 'textarea',
    value: '',
    touched: false,
    valid: false,
    minLength: 10,
    intlId: 'contact-form-message',
    order: 6,
    required: true,
    placeholder: 'employers-contact-message-label',
  },
};
type Props = {
  dictionary: Partial<Dictionaries>;
  locale: Locale;
  page: 'recruitment' | 'assessment' | 'outsourcing';
};

export default function SAndYouForm({ dictionary, locale, page }: Props): JSX.Element {
  const [inputs, setInputs] = useState(defaultInputs);
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const phoneLabelLanguage = usePhoneInputLabelLanguage();
  const { formatMessage } = getI18nUtils(dictionary);

  useEffect(() => {
    if (completed) {
      setTimeout(() => {
        setCompleted(false);
        setInputs(defaultInputs);
        setTermsAccepted(false);
        setFormIsValid(false);
      }, 3000);
    }
  }, [completed]);

  // zelfde regels als POST /business/contact
  function isInputValid(input, inputType: string) {
    const { value, type, minLength, maxLength, required } = input;
    if (value === '' && required) return false;
    if (minLength && value.trim().length < minLength) return false;
    if (maxLength && value.length > maxLength) return false;
    if (type === 'email' && !validateMail(value)) return false;
    if (type === 'tel' && !isValidPhoneNumber(value)) return false;
    if (inputType === 'companyName') return isValidCompanyName(value, minLength);
    if (inputType === 'firstName' || inputType === 'lastName') return isValidPersonName(value, minLength);
    return true;
  }

  const checkFormValidity = () => {
    const validForm: boolean = Object.keys(inputs).every((input) => inputs[input].valid) && termsAccepted;
    setFormIsValid(validForm);
    return validForm;
  };

  async function onSubmitHandler(e) {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage(null);
    if (!checkFormValidity) return;
    const { companyName, firstName, lastName, telephone, message, email } = inputs;
    const body = {
      companyName: companyName.value,
      firstName: firstName.value,
      lastName: lastName.value,
      telephone: telephone.value,
      email: email.value,
      message: message.value,
      language: locale,
      app: 'sandyou.be',
      page,
    };
    try {
      await uploadContactForm(body);
      plausibleEvent('businessContactFormSubmission');
      setCompleted(true);
    } catch (error) {
      logSentryError(error);
      setErrorMessage(formatMessage({ id: 'internal-server-error' }));
    }
    setSubmitting(false);
  }

  // validate the form when an input changes
  useEffect(() => {
    checkFormValidity();
  }, [inputs, termsAccepted]);

  function inputChangedHandler(e, inputType) {
    setErrorMessage(null);
    const inputCp = { ...inputs[inputType] };
    inputCp.touched = true;
    inputCp.value = inputType === 'telephone' ? e : e.target.value;
    inputCp.valid = isInputValid(inputCp, inputType);
    setInputs({ ...inputs, [inputType]: inputCp });
  }

  const sortedFields = Object.keys(inputs).sort((a, b) => (inputs[a].order > inputs[b].order ? 1 : -1));
  return (
    <Card id="contactForm" className={`backgroundMargin${submitting ? ' submitting' : ''}`}>
      <CardBody>
        {!completed ? (
          <>
            <Form onSubmit={(e) => onSubmitHandler(e)}>
              <fieldset disabled={submitting} aria-busy={submitting}>
                <Row>
                  {sortedFields.map((inputType) => {
                    const { type, value, touched, valid, minLength, maxLength, intlId, required, placeholder } =
                      inputs[inputType];
                    return (
                      <Col
                        xs={12}
                        md={intlId === 'contact-form-companyName' || intlId === 'contact-form-message' ? 12 : 6}
                        key={`${inputType}`}
                      >
                        <Label className={`text-muted${required ? ' required' : ''}`} for={inputType}>
                          {formatMessage({ id: intlId })}
                        </Label>
                        {inputType === 'telephone' ? (
                          <PhoneInput
                            className={`${touched && valid ? 'is-valid' : ''} ${touched && !valid ? 'is-invalid' : ''}`}
                            id={inputType}
                            name={inputType}
                            defaultCountry="BE"
                            required={required}
                            value={value}
                            labels={phoneLabelLanguage}
                            international
                            countryCallingCodeEditable={false}
                            // value is stored directly in 'e' so 'e' can be undefined
                            onChange={(e) => e && inputChangedHandler(e, inputType)}
                            onBlur={() => checkFormValidity()}
                          />
                        ) : (
                          <Input
                            name={inputType}
                            id={inputType}
                            value={value}
                            minLength={minLength}
                            maxLength={maxLength}
                            required={required}
                            type={type}
                            valid={touched && valid}
                            invalid={touched && !valid}
                            className="mb-3"
                            onChange={(e) => inputChangedHandler(e, inputType)}
                            onBlur={checkFormValidity}
                            placeholder={placeholder ? formatMessage({ id: placeholder }) : ''}
                          />
                        )}
                      </Col>
                    );
                  })}
                </Row>
                <Row className="mt-3">
                  <Col xs={12}>
                    <FormGroup check inline>
                      <Input
                        type="checkbox"
                        id="terms"
                        name="terms"
                        onChange={() => {
                          setTermsAccepted(!termsAccepted);
                        }}
                        checked={termsAccepted}
                      />
                      <Label for="terms" check>
                        {formatMessage(
                          { id: 'accept-privacy-terms' },
                          {
                            privacyLink: (
                              <a href={`/${locale}/legal/privacy`} target="_blank" rel="noopener noreferrer">
                                {formatMessage({ id: 'privacy_policy' })}
                              </a>
                            ),
                            termsLink: (
                              <a href={`/${locale}/legal/termsandconditions`} target="_blank" rel="noopener noreferrer">
                                {formatMessage({ id: 'termsandconditions' })}
                              </a>
                            ),
                          }
                        )}
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
                {errorMessage && (
                  <Alert color="danger" className="py-2">
                    {errorMessage}
                  </Alert>
                )}
                <Cta className="cta red noArrow" type="submit" disabled={!formIsValid}>
                  {formatMessage({ id: 'contact-form-submit' })}
                </Cta>
              </fieldset>
            </Form>
          </>
        ) : (
          <Alert color="success">{formatMessage({ id: 'contact-form-thanks' })}</Alert>
        )}
      </CardBody>
    </Card>
  );
}
